import React, { useMemo } from "react";
import { merge } from "lodash-es";
import { format } from "date-fns";

import { IEventGroundTransportTravelLegParams, IEventSuggestedLocation } from "Bands/types";
import { FormBuilder } from "shared/components";
import { IFormButtons, IAPIError } from "shared/types";
import {
  useBandRegionalSettings,
  useIsStartDateBeforeEndDate,
  useOnSubmitTravelLeg,
  useTravelLegLocationOptions,
} from "Bands/utils";
import { useEventContext } from "Bands/contexts";
import { createNewDate, getRegionalSettingsOptions, isoToUTCString } from "shared/utils";

import { getFields } from "./formData";

const { defaultTimeZone } = getRegionalSettingsOptions();

interface Props {
  isSubmitting?: boolean;
  error?: string | boolean;
  defaultValues?: IEventGroundTransportTravelLegParams;
  buttons?: IFormButtons;
  apiError?: IAPIError | null;
  handleSubmit: (data: IEventGroundTransportTravelLegParams) => void;
  defaultSuggestedLocations?: IEventSuggestedLocation[];
}

export const EventGroundTransportTravelLegForm: React.FC<Props> = ({
  buttons,
  handleSubmit,
  error,
  defaultValues: defaultValuesProps,
  defaultSuggestedLocations,
  ...rest
}) => {
  const { timeFormat, dateFormat, timeZone } = useBandRegionalSettings();
  const { event } = useEventContext();

  const defaultDate = useMemo(() => {
    if (!event?.date) return null;

    const today = createNewDate();

    const eventDate = createNewDate(
      isoToUTCString({
        iso: event?.date,
      })
    );

    return format(eventDate < today ? today : eventDate, "yyyy-MM-dd");
  }, [event?.date]);

  const defaultValues = useMemo(
    (): IEventGroundTransportTravelLegParams =>
      merge(
        {},
        {
          departure: {
            date: defaultDate,
            timeZone: event?.timeZone || timeZone || defaultTimeZone,
          },
          arrival: {
            date: defaultDate,
            timeZone: event?.timeZone || timeZone || defaultTimeZone,
          },
        },
        defaultValuesProps
      ),
    [defaultValuesProps, event?.timeZone, timeZone, defaultDate]
  );

  const startBeforeEndValues = useMemo(
    () => ({
      startDate: defaultValues.departure?.date,
      startTime: defaultValues.departure?.time,
      startTimezone: defaultValues.departure?.timeZone,
      endDate: defaultValues.departure?.date,
      endTime: defaultValues.departure?.time,
      endTimezone: defaultValues.departure?.timeZone,
    }),
    [defaultValues]
  );

  const { start, end, isStartBeforeEnd, handleStartDateChange, handleEndDateChange } =
    useIsStartDateBeforeEndDate({
      includeEqual: true,
      defaultValues: startBeforeEndValues,
    });

  const {
    options,
    isFetchingLocations,
    resetLocations,
    setSelectedArrival,
    setSelectedDeparture,
    fetchGoogleLocations,
  } = useTravelLegLocationOptions(defaultSuggestedLocations);

  const { onSubmit } = useOnSubmitTravelLeg(handleSubmit, start?.time, end?.time);

  return (
    <FormBuilder
      fields={getFields({
        suggestedLocations: options,
        timeFormat,
        dateFormat,
        isStartBeforeEnd,
        handleStartDateChange,
        handleEndDateChange,
        fetchGoogleLocations,
        isFetchingLocations,
        resetLocations,
        onSelectDeparture: setSelectedDeparture,
        onSelectArrival: setSelectedArrival,
      })}
      onSubmit={onSubmit}
      defaultValues={defaultValues}
      globalError
      submitMode="isDirty"
      apiErrorMessage={error}
      buttons={buttons}
      style={{ gridTemplateColumns: "repeat(6, 1fr)" }}
      {...rest}
    />
  );
};
